import { Layer } from '../types/design';

/**
 * Recursively find a layer by ID
 */
export const findLayerById = (layers: Layer[], id: string): Layer | null => {
  for (const layer of layers) {
    if (layer.id === id) return layer;
    if (layer.children) {
      const found = findLayerById(layer.children, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Update a layer deep in the tree and return the new tree
 */
export const updateLayerDeep = (layers: Layer[], id: string, updates: Partial<Layer>): Layer[] => {
  return layers.map(layer => {
    if (layer.id === id) {
      return { ...layer, ...updates };
    }
    if (layer.children) {
      return { ...layer, children: updateLayerDeep(layer.children, id, updates) };
    }
    return layer;
  });
};

/**
 * Converts a point from one layer's coordinate space to another (absolute/root)
 */
export const getAbsolutePosition = (
  layerId: string,
  canvasRect: DOMRect,
  canvasScale: number
): { left: number; top: number; width: number; height: number } | null => {
  const element = document.getElementById(layerId);
  if (!element) return null;

  const rect = element.getBoundingClientRect();
  
  // To get the unrotated geometric bounds of the element, we use offsetWidth and offsetHeight.
  // The center of the element's AABB matches its geometric center because transform-origin is center center.
  // This bypasses the issue of getBoundingClientRect() returning increasingly large boxes during rotation.
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  
  let width = element.offsetWidth;
  let height = element.offsetHeight;
  
  // Fallback for elements lacking offsetWidth/Height (e.g. inline elements or SVGs)
  if (!width || !height) {
    width = rect.width / canvasScale;
    height = rect.height / canvasScale;
  }
  
  // Map center coordinates to absolute canvas space
  const canvasCenterX = (centerX - canvasRect.left) / canvasScale;
  const canvasCenterY = (centerY - canvasRect.top) / canvasScale;

  return {
    left: canvasCenterX - width / 2,
    top: canvasCenterY - height / 2,
    width,
    height
  };
};

/**
 * Recursively find a layer and its parent
 */
export const findLayerAndParent = (
  layers: Layer[],
  id: string,
  parent: Layer | null = null
): { layer: Layer; parent: Layer | null } | null => {
  for (const layer of layers) {
    if (layer.id === id) return { layer, parent };
    if (layer.children) {
      const found = findLayerAndParent(layer.children, id, layer);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Calculates the bounding box of multiple layers in absolute canvas coordinates
 */
export const getMultiLayerBounds = (
  layerIds: string[],
  canvasRect: DOMRect,
  canvasScale: number
) => {
  let minLeft = Infinity, minTop = Infinity;
  let maxRight = -Infinity, maxBottom = -Infinity;
  let found = false;

  layerIds.forEach(id => {
    const pos = getAbsolutePosition(id, canvasRect, canvasScale);
    if (pos) {
      found = true;
      minLeft = Math.min(minLeft, pos.left);
      minTop = Math.min(minTop, pos.top);
      maxRight = Math.max(maxRight, pos.left + pos.width);
      maxBottom = Math.max(maxBottom, pos.top + pos.height);
    }
  });

  if (!found) return null;

  return {
    left: minLeft,
    top: minTop,
    width: maxRight - minLeft,
    height: maxBottom - minTop
  };
};

/**
 * Ensures that layer positions are updated recursively when a parent moves or is created.
 * This maintains the "Ownership Rule" by syncing logic coordinates with visual state.
 */
export const syncLayerPositions = (
  layers: Layer[],
  layerPositions: Record<string, { left: number; top: number; width?: number; height?: number }>,
  canvasRect: DOMRect,
  canvasScale: number
): Record<string, { left: number; top: number; width?: number; height?: number }> => {
  const nextPositions = { ...layerPositions };

  const sync = (nodes: Layer[]) => {
    nodes.forEach(node => {
      const absPos = getAbsolutePosition(node.id, canvasRect, canvasScale);
      if (absPos) {
        // If it's a group, we might want to preserve its calculated bounds
        nextPositions[node.id] = {
          ...nextPositions[node.id],
          width: absPos.width,
          height: absPos.height
        };
      }
      if (node.children) sync(node.children);
    });
  };

  sync(layers);
  return nextPositions;
};

